import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import api from '../services/api';
import { getFileUrl } from '../utils/fileUtils';
import { FaDollarSign, FaHandHoldingHeart, FaCheckCircle } from 'react-icons/fa';
import './Donaciones.css';

const Donaciones = () => {
  const { data: config = {} } = useQuery({
    queryKey: ['configuracion'],
    queryFn: async () => {
      const response = await api.get('/configuracion');
      return response.data;
    },
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 10 * 60 * 1000,
  });

  const campanaTitulo = useMemo(() => {
    return (config.donaciones_campana_titulo || 'Donaciones').toString().trim();
  }, [config.donaciones_campana_titulo]);

  const [formData, setFormData] = useState({
    donante_nombre: '',
    donante_email: '',
    donante_telefono: '',
    donante_documento: '',
    monto: '',
    tipo_donacion: 'donacion_unica',
    destino: '',
    metodo_pago: 'transferencia',
    observaciones: ''
  });

  const [showSuccess, setShowSuccess] = useState(false);

  const montosSugeridos = [25000, 50000, 100000, 200000, 500000];

  const mutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/donaciones', data);
      return response.data;
    },
    onSuccess: () => {
      setShowSuccess(true);
      setFormData({
        donante_nombre: '',
        donante_email: '',
        donante_telefono: '',
        donante_documento: '',
        monto: '',
        tipo_donacion: 'donacion_unica',
        destino: '',
        metodo_pago: 'transferencia',
        observaciones: ''
      });
      setTimeout(() => setShowSuccess(false), 5000);
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    mutation.mutate({
      ...formData,
      monto: parseFloat(formData.monto)
    });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleMontoClick = (monto) => {
    setFormData({ ...formData, monto: monto.toString() });
  };

  // Si hay campaña configurada, usarla como "destino" (sin estorbar al usuario)
  useEffect(() => {
    if (!campanaTitulo) return;
    setFormData((prev) => {
      // Solo autocompletar si el usuario no escribió nada
      if (prev.destino && prev.destino.trim() !== '') return prev;
      return { ...prev, destino: campanaTitulo };
    });
  }, [campanaTitulo]);

  const scrollToForm = () => {
    document.getElementById('donacion-formulario')?.scrollIntoView({ behavior: 'smooth' });
  };

  const campanaSlogan = (config.donaciones_campana_slogan || 'Una bendición que se comparte. Tu generosidad sostiene la misión y el servicio de nuestra Diócesis.').toString().trim();

  return (
    <div className="donaciones-page">
      <div className="container">
        <div className="donaciones-hero">
          <p className="donaciones-slogan">{campanaSlogan}</p>
          <h1 className="donaciones-titulo">{campanaTitulo.toUpperCase()}</h1>
          <button type="button" className="donaciones-btn-dona" onClick={scrollToForm} aria-label="Ir al formulario de donación">
            <FaHandHoldingHeart />
            <span>Dona</span>
          </button>
        </div>

        {showSuccess && (
          <div className="alert alert-success">
            <FaCheckCircle /> Tu donación ha sido registrada exitosamente. Te enviaremos un recibo por email.
          </div>
        )}

        <div className="donaciones-content">
          <div className="donaciones-info">
            <h2>Campaña actual</h2>
            {config.donaciones_campana_imagen && (
              <div className="donaciones-campana-imagen">
                <img src={getFileUrl(config.donaciones_campana_imagen)} alt={campanaTitulo} />
              </div>
            )}
            <p>
              <strong>{campanaTitulo}</strong>
            </p>
            {config.donaciones_campana_descripcion && (
              <div className="donaciones-campana-descripcion" dangerouslySetInnerHTML={{ __html: config.donaciones_campana_descripcion.replace(/\n/g, '<br />') }} />
            )}
            <p className="donaciones-muted">
              Puedes actualizar esta campaña desde <strong>Administración → Configuración</strong>.
            </p>
          </div>

          <form id="donacion-formulario" onSubmit={handleSubmit} className="donacion-form">
            <h2>Formulario de Donación</h2>

            <div className="form-group">
              <label>Nombre Completo *</label>
              <input
                type="text"
                name="donante_nombre"
                value={formData.donante_nombre}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  name="donante_email"
                  value={formData.donante_email}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Teléfono</label>
                <input
                  type="tel"
                  name="donante_telefono"
                  value={formData.donante_telefono}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Tipo/Destino se manejan por campaña; se envían al backend sin mostrar complejidad */}
            <input type="hidden" name="tipo_donacion" value={formData.tipo_donacion} readOnly />
            <input type="hidden" name="destino" value={formData.destino} readOnly />

            <div className="form-group">
              <label>Monto *</label>
              <input
                type="number"
                name="monto"
                value={formData.monto}
                onChange={handleChange}
                min="0"
                step="1000"
                required
                placeholder="Ingresa el monto"
              />
              <div className="montos-sugeridos">
                <span>Montos sugeridos:</span>
                {montosSugeridos.map(monto => (
                  <button
                    key={monto}
                    type="button"
                    className="btn-monto"
                    onClick={() => handleMontoClick(monto)}
                  >
                    ${monto.toLocaleString()}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label>Método de Pago *</label>
              <select
                name="metodo_pago"
                value={formData.metodo_pago}
                onChange={handleChange}
                required
              >
                <option value="transferencia">Transferencia Bancaria</option>
                <option value="efectivo">Efectivo</option>
                <option value="cheque">Cheque</option>
                <option value="otro">Otro</option>
              </select>
            </div>

            <div className="form-group">
              <label>Observaciones</label>
              <textarea
                name="observaciones"
                value={formData.observaciones}
                onChange={handleChange}
                rows="3"
                placeholder="Información adicional sobre tu donación"
              />
            </div>

            <div className="info-pago">
              <h4>Información para Transferencia:</h4>
              <p><strong>Banco:</strong> {config.donaciones_banco || 'BANCOLOMBIA'}</p>
              <p><strong>Cuenta:</strong> {config.donaciones_cuenta || '88800010489'}</p>
              <p><strong>Tipo:</strong> {config.donaciones_tipo_cuenta || 'Corriente'}</p>
              <p><strong>Titular:</strong> {config.donaciones_titular || 'Diócesis de Ipiales'}</p>
            </div>

            <button type="submit" className="btn btn-primary" disabled={mutation.isPending}>
              {mutation.isPending ? 'Procesando...' : 'Registrar Donación'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Donaciones;
